import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { draftKey, draftPrefix, parseDraft, readDrafts, unsavedDrafts } from '../utils/localDrafts';

const LabAnswersContext = createContext(null);
export const useLabAnswers = () => useContext(LabAnswersContext);

// The lab route keys this provider by labId so state never leaks between labs.
export function LabAnswersProvider({ labId, children }) {
  const [initial] = useState(() => readDrafts(labId));
  const [answers, setAnswers] = useState(initial.answers);
  const [storageError, setStorageError] = useState(initial.error);
  const restoreWarning = useRef(initial.error.startsWith('Some saved answers could not') ? initial.error : '');
  if (!unsavedDrafts.has(labId)) unsavedDrafts.set(labId, new Map());
  const pending = useRef(unsavedDrafts.get(labId));
  const [saveNotice, setSaveNotice] = useState('');

  const persist = useCallback((questionId, answer, isChecked = false, isCorrect = false) => {
    if (!questionId) return;
    const record = { version: 1, currentAnswer: answer ?? '', isChecked, isCorrect, updatedAt: new Date().toISOString() };
    setAnswers(prev => ({ ...prev, [questionId]: record }));
    pending.current.set(questionId, record);
    try {
      // Retry any earlier failed writes while retaining their in-memory values.
      for (const [id, value] of pending.current) {
        window.localStorage.setItem(draftKey(labId, id), JSON.stringify(value));
        pending.current.delete(id);
      }
      if (restoreWarning.current) restoreWarning.current = readDrafts(labId).error;
      setStorageError(restoreWarning.current);
    } catch {
      setStorageError('Some answers are not saved in this browser. Keep this page open and copy your answers before leaving.');
    }
  }, [labId]);

  useEffect(() => {
    function onStorage(event) {
      if (event.key !== null && !event.key.startsWith(draftPrefix(labId))) return;
      if (pending.current.size) {
        setStorageError('Another tab changed saved answers while this page has unsaved edits. Copy your answers before leaving.');
        return;
      }
      if (event.key === null) {
        const restored = readDrafts(labId);
        setAnswers(restored.answers);
        if (restored.error) setStorageError(restored.error);
      } else {
        try {
          // Read the latest committed value, not an older queued storage event.
          const record = parseDraft(window.localStorage.getItem(event.key));
          const id = event.key.slice(draftPrefix(labId).length);
          setAnswers(prev => {
            const next = { ...prev };
            if (record) next[id] = record; else delete next[id];
            return next;
          });
        } catch {
          setStorageError('A saved answer from another tab could not be read. Your visible answers have been kept.');
          return;
        }
      }
      setSaveNotice('Saved answers updated from another tab.');
    }
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, [labId]);

  function clearAll() {
    try {
      const prefix = draftPrefix(labId);
      const keys = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key?.startsWith(prefix)) keys.push(key);
      }
      keys.forEach(key => window.localStorage.removeItem(key));
      pending.current.clear();
      setAnswers({});
      restoreWarning.current = '';
      setStorageError('');
      setSaveNotice('Saved answers for this lab have been cleared.');
      return true;
    } catch {
      setStorageError('Saved answers could not all be cleared. Your visible answers have been kept.');
      return false;
    }
  }

  return <LabAnswersContext.Provider value={{ answers, loading: false, storageError, saveNotice, reportStorageError: setStorageError,
    updateDraft: (id, answer) => persist(id, answer),
    recordAnswer: (id, answer, correct) => persist(id, answer, true, correct), clearAll }}>
    {children}
  </LabAnswersContext.Provider>;
}
